/// <reference types="google.maps" />
import { inject, Inject, Injectable, Optional, DOCUMENT, PLATFORM_ID } from '@angular/core';
import { EMPTY, Subscription } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

import { SawDust } from '@cudirect/sawdust/sawdust';
import { GoogleMapsService } from '@workflow/service/async/google-maps.service';
import { StatefulService } from '@app/service/stateful.service';
import { FieldInstance } from '@workflow/class/field.service';
import { matchDomain } from 'src/utils';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';
import { AnalyticsMetadataService } from '@app/service/analytics-metadata.service';
import { GoogleTrackingService } from '@shared/service/google-tracking.service';

/**
 * Consolidated service for Google Places address autocomplete functionality.
 * Supports both workflow (OLA/OAO) and post-application contexts.
 *
 * This service provides autocomplete functionality for address input fields and maps
 * the selected address details to appropriate application fields or objects.
 *
 * @remarks
 * Workflow-specific analytics services are optional dependencies. When available,
 * enhanced tracking is performed. In post-app contexts, basic event logging is used.
 */
@Injectable({
  providedIn: 'root'
})
export class AddressService {
  private readonly logger = inject(SawDust);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly statefulService = inject(StatefulService);
  private readonly googleMapsService = inject(GoogleMapsService);

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Optional() private readonly workflowService?: WorkflowService,
    @Optional() private readonly analyticsMetadataService?: AnalyticsMetadataService,
    @Optional() private readonly googleTrackingService?: GoogleTrackingService
  ) {}

  /**
   * Enables Google Places Autocomplete functionality on a provided input element and maps
   * the selected address details to either application fields or a post-application object.
   *
   * @param field - Contains information and configuration about the input field.
   * @param inputElem - HTML input element to enable autocomplete on.
   * @param isPostApp - Flag to determine if the function is being called post-application.
   * @param postAppAutoAddressConfig - Configuration mapping for post-application.
   * @param addressObj - Object to store address components during post-application phase.
   */
  public autocomplete(
    field: FieldInstance,
    inputElem: HTMLInputElement | EventTarget,
    isPostApp: boolean = false,
    postAppAutoAddressConfig?: string[],
    addressObj?: any
  ): Subscription {
    const hostname = this.getHostname();
    if (!matchDomain(/^(?:[\w-]+\.)?myori\.com$/, hostname)) {
      // Google Places Autocomplete is disabled for non-myori.com domains
      return EMPTY.subscribe();
    }

    const autoAddressEnabled = field?.config?.autoAddress ?? false;
    if (!autoAddressEnabled && !isPostApp) {
      return EMPTY.subscribe();
    }

    return this.googleMapsService.getGoogleMaps().subscribe(() => {
      if (this.isBrowser) {
        this.initializeAutocomplete(inputElem as HTMLInputElement, field, isPostApp, postAppAutoAddressConfig, addressObj);
      }
    });
  }

  /**
   * Initializes the Google Maps autocomplete widget on the input element.
   */
  private initializeAutocomplete(
    inputElem: HTMLInputElement,
    field: FieldInstance,
    isPostApp: boolean,
    postAppAutoAddressConfig?: string[],
    addressObj?: any
  ): void {
    // Check if dropdown already exists to prevent duplicates
    let dropdownContainer = inputElem.nextSibling as HTMLDivElement;
    if (!dropdownContainer || !dropdownContainer.classList?.contains('google-places-dropdown')) {
      // Create custom dropdown container only if it doesn't exist
      dropdownContainer = this.document.createElement('div');
      dropdownContainer.className = 'google-places-dropdown';

      // Insert dropdown after input element
      if (inputElem.parentElement) {
        inputElem.parentElement.style.position = 'relative';
        inputElem.parentElement.insertBefore(dropdownContainer, inputElem.nextSibling);
      }
    }

    let debounceTimer: any;
    let selectedIndex = -1; // Track currently selected item (-1 = no selection)
    const navigationState = { originalInputValue: '' }; // Store original value for restoration

    // Listen to input events
    inputElem.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const inputValue = inputElem.value.trim();
        if (inputValue.length > 0) {
          selectedIndex = -1; // Reset selection on new input
          navigationState.originalInputValue = inputValue; // Store the typed value
          this.fetchPredictions(inputValue, dropdownContainer, inputElem, field, isPostApp, postAppAutoAddressConfig, addressObj, navigationState);
        } else {
          dropdownContainer.style.display = 'none';
          selectedIndex = -1;
        }
      }, 300); // 300ms debounce
    });

    // Close dropdown when clicking outside
    this.document.addEventListener('click', (event: Event) => {
      if (!inputElem.contains(event.target as Node) && !dropdownContainer.contains(event.target as Node)) {
        dropdownContainer.style.display = 'none';
        selectedIndex = -1;
      }
    });

    // Handle keyboard navigation
    inputElem.addEventListener('keydown', (event: KeyboardEvent) => {
            const items = Array.from(dropdownContainer.querySelectorAll<HTMLDivElement>('.google-places-dropdown-item'));
      const isVisible = dropdownContainer.style.display !== 'none' && items.length > 0;

      if (!isVisible) {
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          selectedIndex = Math.min(selectedIndex + 1, items.length - 1);
          this.updateDropdownHighlight(items, selectedIndex);
          // Update input with highlighted item text
          if (selectedIndex >= 0) {
            inputElem.value = items[selectedIndex].textContent || '';
          }
          break;

        case 'ArrowUp':
          event.preventDefault();
          selectedIndex = Math.max(selectedIndex - 1, -1);
          this.updateDropdownHighlight(items, selectedIndex);
          // Update input with highlighted item text or restore original
          if (selectedIndex >= 0) {
            inputElem.value = items[selectedIndex].textContent || '';
          } else {
            inputElem.value = navigationState.originalInputValue; // Restore original when no selection
          }
          break;

        case 'Enter':
          event.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < items.length) {
            items[selectedIndex].click();
            selectedIndex = -1;
          }
          break;

        case 'Escape':
          dropdownContainer.style.display = 'none';
          inputElem.value = navigationState.originalInputValue; // Restore original value
          selectedIndex = -1;
          break;
      }
    });
  }

  /**
   * Fetches autocomplete predictions from Google Maps API and populates the dropdown.
   */
  private async fetchPredictions(
    input: string,
    dropdownContainer: HTMLDivElement,
    inputElem: HTMLInputElement,
    field: FieldInstance,
    isPostApp: boolean,
    postAppAutoAddressConfig?: string[],
    addressObj?: any,
    navigationState?: { originalInputValue: string }
  ): Promise<void> {
    try {
      // Use the new AutocompleteSuggestion API
      const request = {
        input,
        includedRegionCodes: ['US'],
        locationBias: undefined // Can be configured with user's location if needed
      };

      const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      // Clear previous suggestions
      dropdownContainer.innerHTML = '';

      if (suggestions && suggestions.length > 0) {
        suggestions.forEach((suggestion: google.maps.places.AutocompleteSuggestion) => {
          const item = this.document.createElement('div');
          item.className = 'google-places-dropdown-item';
          // Get text representation of the place
          const mainText = suggestion.placePrediction?.mainText?.toString() || '';
          const secondaryText = suggestion.placePrediction?.secondaryText?.toString() || '';
          
          // Create bold street address and normal secondary text
          if (secondaryText) {
            const boldText = this.document.createElement('strong');
            boldText.textContent = mainText;
            item.appendChild(boldText);
            item.appendChild(this.document.createTextNode(`, ${secondaryText}`));
          } else {
            item.textContent = mainText;
          }

          // Handle selection
          item.addEventListener('click', async () => {
            try {
              const placePrediction = suggestion.placePrediction;
              if (placePrediction) {
                const place = placePrediction.toPlace();
                
                // Fetch place details with address components
                await place.fetchFields({ 
                  fields: ['addressComponents'] 
                });

                // Update input value
                const mainText = placePrediction.mainText?.toString() || '';
                const secondaryText = placePrediction.secondaryText?.toString() || '';
                inputElem.value = secondaryText ? `${mainText}, ${secondaryText}` : mainText;

                // Hide dropdown
                dropdownContainer.style.display = 'none';

                // Handle the place selection
                this.handlePlaceChanged(place, inputElem, field, isPostApp, postAppAutoAddressConfig, addressObj);
                
                // Update originalInputValue to the new value after selection completes
                if (navigationState) {
                  navigationState.originalInputValue = inputElem.value;
                }
              }
            } catch (error: any) {
              const eventState = isPostApp ? 'postapp' : 'application-flow';
              this.logEvent('ecp.ui.usedGoogleAddressPrefill', {
                eventState,
                status: 'error',
                errorMessage: error?.message || 'Unknown error fetching place details'
              });
            }
          });

          dropdownContainer.appendChild(item);
        });

        dropdownContainer.style.display = 'block';
      } else {
        dropdownContainer.style.display = 'none';
      }
    } catch (error: any) {
      this.logger.logEvent('ecp.ui.googlePlacesPredictionError', {
        errorMessage: error?.message || 'Unknown error fetching predictions'
      });
      dropdownContainer.style.display = 'none';
    }
  }

  /**
   * Handles the gmp-select event when user selects an address from dropdown.
   */
  private handlePlaceChanged(
    place: google.maps.places.Place,
    inputElem: HTMLInputElement,
    field: FieldInstance,
    isPostApp: boolean,
    postAppAutoAddressConfig?: string[],
    addressObj?: any
  ): void {
    // Place object is now passed directly (already fetched via fetchFields)

    // Track analytics
    this.trackAnalyticsEvent(isPostApp);

    // Update address component fields (city, state, ZIP)
    this.updateAddressComponentFields(place, field, isPostApp, postAppAutoAddressConfig, addressObj);

    // Update street address field
    this.updateStreetAddress(place, field, inputElem, isPostApp, addressObj);
  }

  /**
   * Tracks analytics event for address autocomplete usage.
   */
  private trackAnalyticsEvent(isPostApp: boolean): void {
    const eventState = isPostApp ? 'postapp' : 'application-flow';

    this.logEvent('ecp.ui.usedGoogleAddressPrefill', {
      eventState,
      status: 'success'
    });

    // Enhanced tracking for workflow context (when services are available)
    if (this.workflowService && this.analyticsMetadataService && this.googleTrackingService) {
      const data = this.analyticsMetadataService.getMetadata(this.workflowService.currentPage);
      this.googleTrackingService.publishEvent(data[0] + '-address-autocomplete', data[1]);
    }
  }

  /**
   * Updates address component fields (city, state, postal code).
   */
  private updateAddressComponentFields(
    place: google.maps.places.Place,
    field: FieldInstance,
    isPostApp: boolean,
    postAppAutoAddressConfig?: string[],
    addressObj?: any
  ): void {
    const addressComponents = ['locality', 'administrative_area_level_1', 'postal_code'];

    addressComponents.forEach((componentType, index) => {
      const componentValue = this.getAddrPart(place, componentType);

      if (isPostApp) {
        if (postAppAutoAddressConfig && addressObj) {
          addressObj[postAppAutoAddressConfig[index]] = componentValue;
        }
      } else {
        const fieldRef = field.config?.autoAddress?.[index];
        if (fieldRef && typeof fieldRef === 'string') {
          const iterationId = String(field.iterationId ?? 0);
          const fieldId = fieldRef.replace('$', iterationId);
          this.statefulService.wrap(fieldId, null, field.pageIterationId).value = componentValue;
        }
      }
    });
  }

  /**
   * Updates the street address field with combined street number and route.
   */
  private updateStreetAddress(
    place: google.maps.places.Place,
    field: FieldInstance,
    inputElem: HTMLInputElement,
    isPostApp: boolean,
    addressObj?: any
  ): void {
    let streetAddress = `${this.getAddrPart(place, 'street_number')} ${this.getAddrPart(place, 'route')}`;
    streetAddress = streetAddress?.trim();

    // Apply field length validation if configured
    if (field.fieldMaxLength > 0 && streetAddress?.length > field.fieldMaxLength) {
      streetAddress = streetAddress.slice(0, field.fieldMaxLength);
    }

    if (streetAddress?.trim()) {
      if (isPostApp) {
        if (addressObj) {
          addressObj.addressLine1 = streetAddress;
        }
        if (this.isBrowser) {
          inputElem.focus();
          inputElem.blur();
        }
      } else {
        field.data.value = streetAddress;
        inputElem.value = streetAddress;
      }
    } else {
      // Clear existing value if no street address found
      inputElem.value = '';
      if (this.isBrowser) {
        inputElem.focus();
      }
    }
  }

  /**
   * Extracts a specific component of an address from Google Places result.
   *
   * @param place - Complete address details of a location.
   * @param name - Name of the address component to extract.
   * @returns Extracted address component or an empty string.
   */
  private getAddrPart(place: google.maps.places.Place, name: string): string {
    const component = place.addressComponents?.find((c) => c.types.includes(name));
    return component?.[name === 'administrative_area_level_1' ? 'shortText' : 'longText'] || '';
  }

  /**
   * Centralized logging method to handle SawDust logging.
   * @param eventName The name of the event to log
   * @param eventData The data to include with the event
   */
  private logEvent(eventName: string, eventData: Record<string, unknown>): void {
    this.logger.logEvent(eventName, eventData);
  }

  /**
   * Method to abstract the retrieving of hostname from the document location.
   * This is useful for testing purposes, allowing to mock the document location.
   * @returns The hostname of the current document location.
   */
  private getHostname(): string {
    return this.isBrowser ? this.document.location.hostname : '';
  }

  /**
   * Updates the visual highlighting of dropdown items during keyboard navigation.
   * @param items - Array of dropdown item elements
   * @param selectedIndex - Index of the currently selected item (-1 for no selection)
   */
  private updateDropdownHighlight(items: HTMLDivElement[], selectedIndex: number): void {
    items.forEach((item, index) => {
      if (index === selectedIndex) {
        item.classList.add('keyboard-focused');
      } else {
        item.classList.remove('keyboard-focused');
      }
    });
  }

  /**
   * Unsubscribes from a subscription.
   * @param subscription - The subscription to unsubscribe from.
   */
  public unsubscribe(subscription: Subscription): void {
    subscription.unsubscribe();
  }
}
